import { ReactNode } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './context/ThemeContext'
import { ToastProvider } from './context/ToastContext'
import { PlayerProvider } from './context/PlayerContext'
import { DiscogsProvider } from './context/DiscogsContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ViewSortProvider } from './context/ViewSortContext'

// Thin wrapper so ViewSortProvider can key on the authenticated user ID,
// causing it to remount (and reload the correct per-user preferences) whenever
// the active user changes via login or logout.
function AuthedViewSortProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  return (
    <ViewSortProvider key={user?.id ?? 'guest'}>
      {children}
    </ViewSortProvider>
  )
}

interface AppProvidersProps {
  queryClient: QueryClient
  children: ReactNode
}

// Single composition point for every app-wide context provider. Order is
// load-bearing (e.g. AuthProvider depends on QueryClientProvider via
// useQueryClient, AuthedViewSortProvider depends on AuthProvider via useAuth)
// so keep it identical to the previous manual nesting in main.tsx/App.tsx.
export function AppProviders({ queryClient, children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <DiscogsProvider>
                <PlayerProvider>
                  <AuthedViewSortProvider>
                    {children}
                  </AuthedViewSortProvider>
                </PlayerProvider>
              </DiscogsProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
